<template>
  <div>
    <v-card>
      <v-card-title primary-title class="pb-0">
        <span class="pt-3 font-weight-black primary--text subtitle-1"
          >TEST PLAN SET-UP({{ testSetupList.length }})</span
        >
        <v-spacer></v-spacer>
        <span v-if="hasReleaseNoteCreateEdit" class="text-sm-right">
          <v-btn
            @click="addTestSetup"
            class="pt-2"
            data-cy="testSetupTable__button--create"
            id="create-test-setup-button"
            text
          >
            <v-icon small class="mr-2">fas fa-plus</v-icon>Add Test Set-up
          </v-btn>
        </span>
      </v-card-title>
      <v-card-text class="pt-0" style="border-bottom: 1px solid #ccc;">
        <v-data-table
          :headers="headers"
          :hide-default-footer="true"
          :items="testSetupList"
          disable-pagination
          no-data-text="There are currently no test set-ups for this release note"
        >
          <template v-slot:item="{ index, item }">
            <tr :key="index" v-if="index !== testSetupToEdit.index">
              <td @click="editTestSetup(item, index)" data-cy="testSetupTable__button--edit">
                {{ item.setupIdentifier || "--" }}
              </td>
              <td @click="editTestSetup(item, index)">
                <span v-html="item.setup || '--'"></span>
              </td>
              <td v-if="mode === `edit`" class="text-sm-right">
                <span>
                  <v-btn icon @click="editTestSetup(item, index)" data-cy="testSetupTable__button--edit">
                    <v-icon small>far fa-edit</v-icon>
                  </v-btn>
                  <v-btn icon @click="showDeleteDialog(item, index)" data-cy="testSetupTable__button--deleteDialog">
                    <v-icon small>far fa-trash-alt</v-icon>
                  </v-btn>
                </span>
              </td>
            </tr>
            <tr :key="index" v-if="index === testSetupToEdit.index">
              <td>
                <v-text-field
                  class="mt-4"
                  data-cy="testSetupTable__input--identifier"
                  id="input-identifier"
                  outlined
                  placeholder="Identifier"
                  ref="identifier"
                  v-model="testSetupToEdit.setupIdentifier"
                ></v-text-field>
              </td>
              <td>
                <v-textarea
                  class="mt-4"
                  auto-grow
                  data-cy="testSetupTable__input--setup"
                  id="setup"
                  outlined
                  placeholder="Setup"
                  ref="setup"
                  rows="1"
                  v-model="testSetupToEdit.setup"
                ></v-textarea>
              </td>
              <td>
                <span>
                  <v-btn icon @click="resetTestSetup()" data-cy="testSetupTable__input--cancel">
                    <v-icon>fas fa-times</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    @click="saveTestSetup(index)"
                    data-cy="testSetupTable__input--save"
                    :disabled="testSetupToEdit.setup === null || testSetupToEdit.setup.length == 0"
                  >
                    <v-icon>fas fa-check-circle</v-icon>
                  </v-btn>
                </span>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-dialog v-model="isDialogVisible" persistent max-width="490">
      <v-card>
        <v-card-title class="headline">Delete Test Set-up?</v-card-title>
        <v-card-text>
          <div>Do you want to delete the following test set-up?</div>
          <div class="och-container--info mt-2">
            {{ testSetupToDelete.setupIdentifier }}: {{ testSetupToDelete.setup }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="isDialogVisible = false" data-cy="testSetupTable__button--cancel" outlined>Cancel</v-btn>
          <v-btn
            @click="deleteTestSetup(testSetupToDelete)"
            color="red darken-2"
            dark
            data-cy="testSetupTable__button--delete"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "individualTestSetupTable",
  computed: {
    releaseNoteId() {
      return this.$route.params.releaseNoteId;
    },
    testSetupList() {
      return this.$store.state.releaseNote.releaseNoteRecord.individualSetup || [];
    },
    ...mapGetters({
      hasReleaseNoteCreateEdit: "session/hasReleaseNoteCreateEdit",
    }),
  },
  created() {
    if (this.mode === "edit") {
      this.headers.push({
        text: "",
        align: "left",
        sortable: false,
        value: "",
      });
    }
  },
  components: {},
  data() {
    return {
      isDirty: false,
      headers: [
        {
          text: "Identifier",
          align: "left",
          sortable: false,
          value: "setupIdentifier",
        },
        {
          text: "Setup",
          align: "left",
          sortable: false,
          value: "setup",
        },
      ],
      search: null,
      isDialogVisible: false,
      isSubmitting: false,
      setupValid: false,
      testSetupToAdd: {},
      testSetupToEdit: {},
      testSetupToDelete: {},
    };
  },
  methods: {
    addTestSetup() {
      const testSetup = {
        individualSetup: null,
        setup: null,
      };
      this.testSetupList.push(testSetup);
      const index = this.testSetupList.length - 1;
      this.editTestSetup(testSetup, index);
    },
    async deleteTestSetup(testSetup) {
      this.isDialogVisible = false;
      this.testSetupList.splice(testSetup.index, 1);
      try {
        await this.$store.dispatch("releaseNote/updateIndividualSetup", {
          releaseNoteId: this.releaseNoteId,
          testSetupList: this.testSetupList,
        });
        // Refresh audit log to show the deletion
        await this.$store.dispatch("auditLog/fetchReleaseNote", this.releaseNoteId);
      } catch (e) {
        console.error(e);
      } finally {
        await this.resetTestSetup();
      }
    },
    editTestSetup(testSetup, index) {
      this.testSetupToEdit = Object.assign({}, testSetup, { index });
      setTimeout(() => {
        this.$refs.identifier.focus();
      });
    },
    async saveTestSetup(index) {
      this.testSetupList[index] = this.testSetupToEdit;
      try {
        await this.$store.dispatch("releaseNote/updateIndividualSetup", {
          releaseNoteId: this.releaseNoteId,
          testSetupList: this.testSetupList,
        });
        // Refresh audit log to show the update
        await this.$store.dispatch("auditLog/fetchReleaseNote", this.releaseNoteId);
      } catch (e) {
        console.error(e);
      } finally {
        await this.resetTestSetup();
      }
    },
    async resetTestSetup() {
      await this.$store.dispatch("releaseNote/getReleaseNoteRecord", this.releaseNoteId);
      this.testSetupToEdit = {};
    },
    showDeleteDialog(testSetup, index) {
      testSetup.index = index;
      this.testSetupToDelete = testSetup;
      this.isDialogVisible = true;
    },
  },
  props: ["mode", "testSetupItems"],
};
</script>

<style scoped>
.och-table__row--deleted {
  color: #777;
  font-style: italic;
  background-color: #eee;
}

.och-container--info {
  background-color: #eee;
  border-radius: 5px;
  padding: 20px;
}
</style>
